import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { JobsView } from './JobsView.js';
import { useJobsQuery } from './useJobsQuery.js';

/**
 * The `/jobs` route component (promoted from a ComingSoon stub this plan). Reads the typed search
 * params (`status`, `show_archived`), fires the coarse-endpoint TanStack Query, and renders the
 * faithful `JobsView` list on success. Loading → a page-shaped `RouteSkeleton`; error → announced error
 * state with a retry that refetches (the reports `ReportsRoute` 3-state contract).
 *
 * The server computes the job list + per-job rollups + the status counts; this component never
 * re-aggregates and never sends a tenant id (SEC-11).
 */

const routeApi = getRouteApi('/jobs');

export function JobsRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useJobsQuery(search);

  if (query.isPending) {
    return <RouteSkeleton label={t('jobs:title')} rows={8} columns={5} />;
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('jobs:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('jobs:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('jobs:error.retry')}
        </button>
      </section>
    );
  }

  return <JobsView data={query.data} search={search} />;
}
